/**
 * menuItemManager.ts - Manage menu items with Selectr integration
 * 
 * CHANGE HISTORY:
 * - 251114_2050 [complete] danielbeer: Initial creation with Selectr integration
 */

import type { MenuItem } from '../types/menu.types';

export class MenuItemManager {
  private items: Map<string, MenuItem>;
  private order: string[];
  
  constructor(initialItems: MenuItem[] = []) {
    this.items = new Map();
    this.order = [];
    
    initialItems.forEach(item => this.addItem(item));
  }
  
  /**
   * Add a menu item
   */
  addItem(item: MenuItem, parentId?: string): void {
    if (parentId) {
      const parent = this.items.get(parentId);
      if (parent && parent.type === 'submenu') {
        parent.children = [...(parent.children || []), item];
      }
    } else {
      this.items.set(item.id, item);
      this.order.push(item.id);
    }
  }
  
  /**
   * Remove a menu item
   */
  removeItem(id: string): void {
    this.items.delete(id);
    this.order = this.order.filter(itemId => itemId !== id);
    
    // Remove from any parent's children
    this.items.forEach(item => {
      if (item.children) {
        item.children = item.children.filter(child => child.id !== id);
      }
    });
  }
  
  /**
   * Update a menu item
   */
  updateItem(id: string, updates: Partial<MenuItem>): void {
    const item = this.items.get(id);
    if (item) {
      Object.assign(item, updates);
    }
  }
  
  /**
   * Get item by ID
   */
  getItem(id: string): MenuItem | undefined {
    return this.items.get(id);
  }
  
  /**
   * Get all items
   */
  getAllItems(): MenuItem[] {
    return this.order
      .map(id => this.items.get(id))
      .filter((item): item is MenuItem => item !== undefined);
  }
  
  /**
   * Get items sorted by order
   */
  getSortedItems(): MenuItem[] {
    return this.getAllItems().sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });
  }
  
  /**
   * Get items by category
   */
  getByCategory(category: string): MenuItem[] {
    return this.getAllItems().filter(item => item.category === category);
  }
  
  /**
   * Get visible items (not hidden)
   */
  getVisibleItems(): MenuItem[] {
    return this.getAllItems().filter(item => !item.hidden);
  }
  
  /**
   * Search items by label or tooltip
   */
  searchItems(query: string): MenuItem[] {
    const lowerQuery = query.toLowerCase();
    return this.getAllItems().filter(item => 
      item.label.toLowerCase().includes(lowerQuery) ||
      item.tooltip?.toLowerCase().includes(lowerQuery)
    );
  }
  
  /**
   * Reorder items
   */
  reorderItems(ids: string[]): void {
    this.order = ids;
    ids.forEach((id, index) => {
      const item = this.items.get(id);
      if (item) {
        item.order = index;
      }
    });
  }
  
  /**
   * Get categories
   */
  getCategories(): string[] {
    const categories = new Set<string>();
    this.getAllItems().forEach(item => {
      if (item.category) {
        categories.add(item.category);
      }
    });
    return Array.from(categories);
  }
  
  /**
   * Toggle item expanded state
   */
  toggleExpanded(id: string): void {
    const item = this.items.get(id);
    if (item && item.type === 'submenu') {
      item.expanded = !item.expanded;
    }
  }
  
  /**
   * Set item expanded state
   */
  setExpanded(id: string, expanded: boolean): void {
    const item = this.items.get(id);
    if (item && item.type === 'submenu') {
      item.expanded = expanded;
    }
  }
  
  /**
   * Expand all items
   */
  expandAll(): void {
    this.items.forEach(item => {
      if (item.type === 'submenu') {
        item.expanded = true;
      }
    });
  }
  
  /**
   * Collapse all items
   */
  collapseAll(): void {
    this.items.forEach(item => {
      if (item.type === 'submenu') {
        item.expanded = false;
      }
    });
  }
  
  /**
   * Clone manager with filtered items
   */
  filter(predicate: (item: MenuItem) => boolean): MenuItemManager {
    const filtered = this.getAllItems().filter(predicate);
    return new MenuItemManager(filtered);
  }
  
  /**
   * Export items as JSON
   */
  toJSON(): MenuItem[] {
    return this.getSortedItems();
  }
  
  /**
   * Import items from JSON
   */
  fromJSON(items: MenuItem[]): void {
    this.items.clear();
    this.order = [];
    items.forEach(item => this.addItem(item));
  }
}
